import { create }      from 'zustand'
import { getSupabase } from '../util/supabase-utils'
import {logger}        from "../util/log-utils"
import {setUserId, trackLogin} from '../util/analytics'
import {readThemePreference, writeThemePreference} from '../util/theme-preference'

const log = logger("useUserStore", 1)

export const useUserStore = create((set, get) => {
	const supabase = getSupabase()

	// Initialize the session from Supabase on startup
	const initializeSession = async () => {
		try {
			const { data, error } = await supabase.auth.getSession()
			if (error) {
				log.error("Error initializing session:", error)
				set({ authLoaded: true })
				return
			}

			if (data.session) {
				set({
					session: data.session,
					user: data.session.user,
					authLoaded: true
				})
			}
			else {
				set({
					session: null,
					user: null,
					authLoaded: true,
					signedOutByUser: false
				})
			}
		} catch (err) {
			log.error("Exception during session initialization:", err)
			set({ authLoaded: true })
		}
	}

	// Listen for auth state changes
	const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
		log.debug(`supabase.auth.onAuthStateChange ${event}`)

		if (event === 'SIGNED_OUT') {
			const { signedOutByUser } = get()

			set({
				session: null,
				user: null
				// intentionally preserve signedOutByUser
			})

			// stop attributing events to someone who has signed out (094)
			setUserId(null)

			log.debug(
				signedOutByUser
					? 'User explicitly signed out'
					: 'Session expired or revoked'
			)
		}
		else if (event === 'SIGNED_IN') {
			const newUser = session?.user
			const oldUser = get().user
			if (!oldUser || oldUser.id !== newUser?.id) {
				set({
					session,
					user: newUser,
					signedOutByUser: false
				})

				// analytics identity (094)
				setUserId(newUser.id)
				trackLogin()
			}
		}
		else if (event === 'TOKEN_REFRESHED') {
			set({ session, user: session.user })
		}
	})

	// Initialize the session on startup
	initializeSession()

	return {
		session: null,
		user: null,
		authLoaded: false,
		signedOutByUser: false,
		setSession: (session) => set(() => ({ session, user: session?.user })),
		signOut: async () => {
			set({ signedOutByUser: true })
			const { error } = await supabase.auth.signOut()
			if (error) {
				log.error("Error signing out:", error)
			}
		},
		cleanup: () => {
			subscription.unsubscribe() // Ensure subscription cleanup
		}
	}
})

/*
 * The light / dark / auto preference (196).
 *
 * A store rather than a prop: App.js owns the theme, and the one place that needs to CHANGE the
 * preference -- the profile setup dialog -- sits behind AuthGate, AppLayout and PrepKit. Threading a
 * setter down five layers to be used once is worse than a store beside useUserStore.
 *
 * Writes through to localStorage on every set, so the choice survives a reload. That is new: before
 * this, a theme choice did not outlive the page.
 */
export const useThemeStore = create((set) => ({
	preference: readThemePreference(),
	setPreference: (preference) => {
		writeThemePreference(preference)
		set({preference})
	},
}))
